// ════════════════════════════════════════════════════════════
//  Arena.jsx — THE FIELD: a shard of the Unstable World, adrift
// ════════════════════════════════════════════════════════════
//  caP'n named no place for this fight, so the standing doctrine
//  answers "what is out there": the Unstable World (ie-unstable-
//  world). The field itself is a broken plate of obsidian hanging
//  over an ocean of Khao, its seams lit from below.
//
//  The Four are carved into the rim at their canon places — Khao
//  top, Anima bottom, Lyf left, Lyt right (I_UNIVERSE: "shape is
//  free, colour and position are not") — read from the default
//  camera, which sits behind your side.
//
//  Laws kept: zero lights added by the backdrop · fog={false} on
//  everything past the field · seeded, never random · one useFrame
//  per system.
//
//  ── THE BLENDER FIELD (2026-10-20, caP'n: "Redo the shatterpoof
//  arena in blender") ─────────────────────────────────────────
//  The plate, its cracks, the inlays, the rim, the spire and the
//  debris are now ONE glb built in Blender:
//    builder  gamemaster/models/sculpt/builders/shatterproof_arena_blender/build_arena.py
//    master   gamemaster/models/sculpt/glb/shatterproof_arena.glb
//    here     public/models/shatterproof_arena.glb  (byte-identical copy — never edit it here)
//  Its contract: every slab top is exactly y 0, plate radius FIELD_R,
//  zero lights. The sky dome and the Unstable World stay here (they
//  are the doctrine's, not the field's). The painted plate below is
//  THE FAIL-SAFE: it shows while the glb loads, or if it never does.
// ════════════════════════════════════════════════════════════
import { Component, Suspense, useEffect, useMemo, useRef } from "react";
import { useFrame, useLoader } from "@react-three/fiber";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import * as THREE from "three";
import { canvasTex, mulberry32 } from "../ieBeingModels.jsx";
import UnstableWorld, { IE_LIGHT } from "../unstableWorld.jsx";
import { ARENA_R } from "../sim/battle.js";

export const FIELD_R = ARENA_R + 1.3;   // the plate is a little wider than where beings can walk
export const BACKDROP_BOUNDS = { cx: 0, cz: 0, radius: 15, minH: 0, maxH: 3 };
export const DOME_R = 3.4 * BACKDROP_BOUNDS.radius;   // every element stays under this (measured: eclipse 2.19R, hole 2.34R)

// THE FOUR — canon colours, canon places
const FOUR = [
  { name: "Khao",  color: "#A855F7", x: 0, z: -1 },   // top
  { name: "Anima", color: "#F2B134", x: 0, z: 1 },    // bottom
  { name: "Lyf",   color: "#4CC9F0", x: -1, z: 0 },   // left
  { name: "Lyt",   color: "#FF4D3D", x: 1, z: 0 },    // right
];

// The floor: obsidian plates split by glowing seams, a duel circle,
// and the scuffed ring where every fight is fought.
function floorTex() {
  return canvasTex("clash_floor_v2", 1024, 1024, (g, w) => {
    const R = mulberry32(2026);
    const c = w / 2;
    const base = g.createRadialGradient(c, c, 0, c, c, c);
    base.addColorStop(0, "#2a2238");
    base.addColorStop(0.7, "#1c1628");
    base.addColorStop(1, "#120d1c");
    g.fillStyle = base; g.fillRect(0, 0, w, w);
    // stone grain
    for (let i = 0; i < 9000; i++) {
      const v = R();
      g.fillStyle = v > 0.5 ? `rgba(120,100,160,${R() * 0.05})` : `rgba(0,0,0,${R() * 0.08})`;
      g.fillRect(R() * w, R() * w, 1 + R() * 3, 1 + R() * 3);
    }
    // the shatter: seams radiating from off-centre points (Voronoi-ish
    // by drawing jagged polylines) — dark gap, then a faint Khao glow
    const seam = (pts, glow) => {
      g.lineJoin = "round";
      g.strokeStyle = "rgba(4,2,8,0.9)"; g.lineWidth = 7; g.shadowBlur = 0;
      g.beginPath(); pts.forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y))); g.stroke();
      g.strokeStyle = glow; g.lineWidth = 1.6; g.shadowColor = glow; g.shadowBlur = 10;
      g.beginPath(); pts.forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y))); g.stroke();
      g.shadowBlur = 0;
    };
    for (let i = 0; i < 26; i++) {
      const a = R() * Math.PI * 2;
      let x = c + Math.cos(a) * (60 + R() * 120), y = c + Math.sin(a) * (60 + R() * 120);
      const pts = [[x, y]];
      let dir = a + (R() - 0.5) * 0.6;
      const steps = 6 + Math.floor(R() * 8);
      for (let k = 0; k < steps; k++) {
        dir += (R() - 0.5) * 0.8;
        x += Math.cos(dir) * (30 + R() * 34); y += Math.sin(dir) * (30 + R() * 34);
        pts.push([x, y]);
      }
      seam(pts, R() < 0.7 ? "rgba(124,231,255,0.55)" : "rgba(179,136,255,0.55)");
    }
    // the duel circle
    g.strokeStyle = "rgba(242,193,78,0.45)"; g.lineWidth = 3;
    g.beginPath(); g.arc(c, c, c * 0.93, 0, 7); g.stroke();
    g.strokeStyle = "rgba(242,193,78,0.22)"; g.lineWidth = 1.5;
    g.beginPath(); g.arc(c, c, c * 0.89, 0, 7); g.stroke();
    // centre seal — two rings and a shattered star
    g.strokeStyle = "rgba(255,233,160,0.28)"; g.lineWidth = 2.5;
    g.beginPath(); g.arc(c, c, 92, 0, 7); g.stroke();
    g.beginPath(); g.arc(c, c, 70, 0, 7); g.stroke();
    g.beginPath();
    for (let i = 0; i <= 8; i++) {
      const a = (i / 8) * Math.PI * 2 - Math.PI / 2, r = i % 2 ? 34 : 70;
      const x = c + Math.cos(a) * r, y = c + Math.sin(a) * r;
      i ? g.lineTo(x, y) : g.moveTo(x, y);
    }
    g.stroke();
    // THE FOUR, at their places on the rim
    for (const f of FOUR) {
      const x = c + f.x * c * 0.86, y = c + f.z * c * 0.86;
      const gr = g.createRadialGradient(x, y, 0, x, y, 60);
      gr.addColorStop(0, f.color + "88"); gr.addColorStop(1, f.color + "00");
      g.fillStyle = gr; g.beginPath(); g.arc(x, y, 60, 0, 7); g.fill();
      g.strokeStyle = f.color; g.lineWidth = 3; g.shadowColor = f.color; g.shadowBlur = 16;
      g.beginPath(); g.arc(x, y, 22, 0, 7); g.stroke();
      g.beginPath(); g.moveTo(x, y - 30); g.lineTo(x + 12, y); g.lineTo(x, y + 30); g.lineTo(x - 12, y); g.closePath(); g.stroke();
      g.shadowBlur = 0;
    }
  });
}

// The sky: a dome that already has depth before anything stands in
// front of it — gradient, nebula bands, the hairline fractures, stars.
function skyTex() {
  return canvasTex("clash_sky_v1", 2048, 1024, (g, w, h) => {
    const R = mulberry32(77);
    const grad = g.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, "#030208");
    grad.addColorStop(0.38, "#0b0618");
    grad.addColorStop(0.5, "#1d0f30");
    grad.addColorStop(0.6, "#0c0818");
    grad.addColorStop(1, "#040309");
    g.fillStyle = grad; g.fillRect(0, 0, w, h);
    // nebula bands — N'ullus violet and O'mnis gold, faint
    for (let i = 0; i < 16; i++) {
      const y = h * (0.25 + R() * 0.3), hh = 30 + R() * 110;
      const col = R() < 0.6 ? "179,136,255" : "242,193,78";
      const band = g.createLinearGradient(0, y - hh, 0, y + hh);
      band.addColorStop(0, `rgba(${col},0)`); band.addColorStop(0.5, `rgba(${col},${0.04 + R() * 0.09})`); band.addColorStop(1, `rgba(${col},0)`);
      g.fillStyle = band;
      g.fillRect(R() * w * 0.5, y - hh, w * (0.4 + R() * 0.6), hh * 2);
    }
    // the wound, running out across the whole sky
    for (let i = 0; i < 14; i++) {
      g.strokeStyle = `rgba(255,77,141,${0.05 + R() * 0.1})`; g.lineWidth = 1 + R();
      let x = R() * w, y = h * (0.15 + R() * 0.4);
      g.beginPath(); g.moveTo(x, y);
      for (let k = 0; k < 9; k++) { x += (R() - 0.3) * 120; y += (R() - 0.5) * 50; g.lineTo(x, y); }
      g.stroke();
    }
    for (let i = 0; i < 900; i++) {
      const y = R() * h * 0.62;
      g.fillStyle = `rgba(255,255,255,${0.15 + R() * 0.6})`;
      const s = R() < 0.95 ? 1 : 2;
      g.fillRect(R() * w, y, s, s);
    }
  });
}

// the underside of the plate: a jagged inverted spire of rock, its
// seams leaking Khao. Seeded jitter so it is the same rock every load.
function useSpireGeo() {
  return useMemo(() => {
    const geo = new THREE.ConeGeometry(FIELD_R, 4.2, 18, 6, true);
    geo.rotateX(Math.PI);
    const R = mulberry32(5150);
    const p = geo.attributes.position;
    for (let i = 0; i < p.count; i++) {
      const y = p.getY(i);
      if (y > 2.05) continue;          // keep the rim exact so it meets the plate
      const k = 0.18 + R() * 0.22;
      p.setX(i, p.getX(i) * (1 + (R() - 0.5) * k));
      p.setZ(i, p.getZ(i) * (1 + (R() - 0.5) * k));
      p.setY(i, y + (R() - 0.5) * 0.35);
    }
    geo.computeVertexNormals();
    geo.translate(0, -2.1 - 0.3, 0);
    return geo;
  }, []);
}

function rockTex() {
  return canvasTex("clash_rock_v1", 512, 512, (g, w, h) => {
    const R = mulberry32(31);
    g.fillStyle = "#140f1e"; g.fillRect(0, 0, w, h);
    for (let i = 0; i < 4000; i++) {
      g.fillStyle = R() > 0.5 ? `rgba(80,60,120,${R() * 0.12})` : `rgba(0,0,0,${R() * 0.2})`;
      g.fillRect(R() * w, R() * h, 2 + R() * 5, 2 + R() * 9);
    }
    for (let i = 0; i < 18; i++) {
      g.strokeStyle = "rgba(124,231,255,0.7)"; g.lineWidth = 1.5; g.shadowColor = "#7CE7FF"; g.shadowBlur = 8;
      let x = R() * w, y = 0;
      g.beginPath(); g.moveTo(x, y);
      while (y < h) { x += (R() - 0.5) * 30; y += 20 + R() * 30; g.lineTo(x, y); }
      g.stroke();
    }
    g.shadowBlur = 0;
  });
}

export function Lights() {
  return (
    <>
      <hemisphereLight args={["#b9a6ff", "#1a0f22", 0.75]} />
      <ambientLight intensity={0.25} />
      {/* key: warm, high, from your side */}
      <directionalLight position={[6, 12, 9]} intensity={2.1} color="#fff0dc" />
      {/* rim: cool Khao, from the far side — it is what makes the
          silhouettes read against a dark sky */}
      <directionalLight position={[-7, 6, -10]} intensity={1.3} color="#7ce7ff" />
    </>
  );
}

export const ARENA_GLB_URL = `${process.env.PUBLIC_URL || ""}/models/shatterproof_arena.glb`;

// ── THE SPECIAL ARENAS (2026-10-20, caP'n: "plan for special arenas like the
// circuit and contenders should have their own and the champions cup etc") ──
// One Blender builder, one field, a VARIANT per occasion (build_arena.py
// VARIANTS). "shard" is the everyday field. Looks are placeholders for caP'n.
export const ARENAS = {
  shard: "The Shard",                // everyday: practice, friends, the Climb
  circuit: "The Circuit",            // a Circuit league bout
  contenders: "The Contenders",      // a Contenders league bout (and Kings Road)
  main: "The Main Event",            // the hour's Main Event
  winners: "The Winners' Cup",       // Saturday
  losers: "The Losers' Cup",         // Thursday (V2: Saturday)
  finale: "The Season Finale",
  apocalypse: "Apocalypse",          // the Circuit against the Contenders
};
export const arenaUrl = (v) => `${process.env.PUBLIC_URL || ""}/models/shatterproof_arena${!v || v === "shard" ? "" : "_" + v}.glb`;
/** Which field a League bout is fought on — a bout row ({k, dv, fx, c}) as the stage and the boards carry it. */
export function arenaOfBout(bt) {
  if (!bt) return "shard";
  if (bt.k === "apo") return "apocalypse";
  // V2 (2026-10-21): the Champions Cup has the Winners' field · the Circuit Cup's 10pm final gets the gold Main Event field
  if (bt.k === "cup") return bt.c === "w" || bt.c === "x" ? "winners" : bt.c === "l" ? "losers" : bt.c === "f" ? "finale" : bt.c === "k" ? "contenders" : bt.c === "c" && bt.r === 3 ? "main" : "circuit";
  if (bt.fx === "main") return "main";
  return bt.dv === "k" ? "contenders" : "circuit";
}
/** …and for a match spec (League bouts carry kind "bt" + the bout row; anything else is the Shard). */
export const arenaFor = (spec) => (spec && spec.kind === "bt" ? arenaOfBout(spec.bout) : "shard");

// Pulses — dials. The glb's emissive strengths are the rest values; these
// only breathe around them.
const RIM_PULSE = 0.18, SEAM_PULSE = 0.12, DEBRIS_BOB = 0.28;

// Read the glb ONCE: find the named parts the field animates, and set
// what glTF cannot say (a glow pool must not write depth).
function prepareField(scene) {
  const root = scene.clone(true);
  const pulse = [], debris = [];
  root.traverse((o) => {
    // ⚠ the variants are scenes of ONE .blend, so Blender suffixed their names
    // ("plate.001") and the loader sanitized that to "plate001" — read the BASE name
    const n = o.name.replace(/\d{3}$/, "");
    if (o.isMesh) {
      o.material = o.material.clone();     // the cache's material is never mutated
      if (n === "plate") o.receiveShadow = true;
      if (n.startsWith("glow_")) { o.material.depthWrite = false; o.renderOrder = 1; }
      if (n === "rim_burn") pulse.push({ m: o.material, base: o.material.emissiveIntensity, amp: RIM_PULSE, rate: 0.8, ph: 0 });
      if (n === "seams") pulse.push({ m: o.material, base: o.material.emissiveIntensity, amp: SEAM_PULSE, rate: 0.5, ph: 1 });
    }
    if (o.name.startsWith("debris_")) {
      const ex = o.userData || {};
      debris.push({ o, y0: o.position.y, r0: o.rotation.y, ph: ex.phase || 0, spin: ex.spin || 0.08 });
    }
  });
  return { root, pulse, debris };
}

function BlenderField({ url }) {
  const gltf = useLoader(GLTFLoader, url);
  const F = useMemo(() => prepareField(gltf.scene), [gltf]);
  // the clones are ours (the loader's cached originals are never touched) — free them on the way out
  useEffect(() => () => F.root.traverse((o) => { if (o.isMesh) o.material.dispose(); }), [F]);
  useFrame(({ clock }) => {           // ONE frame hook for the whole field
    const t = clock.elapsedTime;
    for (const p of F.pulse) p.m.emissiveIntensity = p.base * (1 + Math.sin(t * p.rate + p.ph) * p.amp);
    for (const d of F.debris) {
      d.o.position.y = d.y0 + Math.sin(t * 0.35 + d.ph) * DEBRIS_BOB;
      d.o.rotation.y = d.r0 + t * d.spin;
    }
  });
  return <primitive object={F.root} />;
}

class FieldBoundary extends Component {
  constructor(p) { super(p); this.state = { bad: false }; }
  static getDerivedStateFromError() { return { bad: true }; }
  componentDidCatch(e) { console.warn(`[arena] ${this.props.url} did not load — the painted plate holds the fight`, e); } // eslint-disable-line no-console
  render() { return this.state.bad ? this.props.fallback : this.props.children; }
}

// `bounds` lets a bigger room (Ringside's stadium) push the whole Unstable World
// out past its walls. The fight passes nothing, so the fight's sky is unchanged.
export default function Arena({ bounds = BACKDROP_BOUNDS, variant = "shard" } = {}) {
  const url = arenaUrl(ARENAS[variant] ? variant : "shard");
  const domeR = 3.4 * bounds.radius;
  const sky = useMemo(() => skyTex(), []);
  const painted = <PaintedField />;
  return (
    <group>
      {/* the sky dome */}
      <mesh renderOrder={-1}>
        <sphereGeometry args={[domeR, 48, 24]} />
        <meshBasicMaterial map={sky} side={THREE.BackSide} fog={false} depthWrite={false} toneMapped={false} />
      </mesh>
      <UnstableWorld bounds={bounds} seed={4}
        sky={{ oceanTint: IE_LIGHT.khao, eclipseRim: IE_LIGHT.omnis, eclipseCrack: IE_LIGHT.anima, holeHot: IE_LIGHT.ruin, holeCool: IE_LIGHT.nullus }} />
      <FieldBoundary key={url} url={url} fallback={painted}>
        <Suspense fallback={painted}>
          <BlenderField url={url} />
        </Suspense>
      </FieldBoundary>
    </group>
  );
}

// THE FAIL-SAFE — the painted plate the field was before Blender, unchanged.
function PaintedField() {
  const floor = useMemo(() => floorTex(), []);
  const rock = useMemo(() => rockTex(), []);
  const spire = useSpireGeo();
  const rimRef = useRef();
  const seamRef = useRef();
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    if (rimRef.current) rimRef.current.material.opacity = 0.55 + Math.sin(t * 0.8) * 0.12;
    if (seamRef.current) seamRef.current.material.opacity = 0.18 + Math.sin(t * 0.5 + 1) * 0.06;
  });
  return (
    <group>
      {/* the plate */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <circleGeometry args={[FIELD_R, 96]} />
        <meshStandardMaterial map={floor} roughness={0.82} metalness={0.08} />
      </mesh>
      {/* its thickness */}
      <mesh position={[0, -0.15, 0]}>
        <cylinderGeometry args={[FIELD_R, FIELD_R * 0.98, 0.3, 96, 1, true]} />
        <meshStandardMaterial map={rock} roughness={0.9} side={THREE.DoubleSide} />
      </mesh>
      {/* the spire below */}
      <mesh geometry={spire}>
        <meshStandardMaterial map={rock} roughness={0.95} emissive="#2a1648" emissiveIntensity={0.25} side={THREE.DoubleSide} />
      </mesh>
      {/* a glow leaking out of the underside */}
      <mesh ref={seamRef} geometry={spire} scale={[1.04, 1.02, 1.04]}>
        <meshBasicMaterial color="#7CE7FF" transparent opacity={0.18} side={THREE.BackSide} depthWrite={false} toneMapped={false} />
      </mesh>
      {/* the burning rim */}
      <mesh ref={rimRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
        <ringGeometry args={[FIELD_R - 0.08, FIELD_R + 0.05, 128]} />
        <meshBasicMaterial color="#F2C14E" transparent opacity={0.6} toneMapped={false} />
      </mesh>
    </group>
  );
}
